import { useCallback, useEffect, useRef, useState } from 'react'
import debug from 'debug'

export interface PromptClickHandlerProps {
    promptId: string
    onOutsideClick?: () => void
    onPromptHide?: () => void
    onPromptShow?: () => void
    defaultHidden?: boolean
    shouldReadFromLocalStorage?: boolean
    disableOutsideClick?: boolean
    debug?: debug.Debugger
}

export const usePromptClickHandler = ({
    promptId,
    onOutsideClick,
    onPromptHide,
    onPromptShow,
    defaultHidden = true,
    shouldReadFromLocalStorage = false,
    disableOutsideClick = false,
    debug,
}: PromptClickHandlerProps) => {
    const shouldDisplayFromStorage =
        typeof window !== 'undefined' &&
        window.localStorage &&
        window.localStorage.getItem(promptId) === 'true'

    const [hidden, setHidden] = useState(
        shouldReadFromLocalStorage ? !shouldDisplayFromStorage : defaultHidden,
    )
    const backgroundComponentRef = useRef<HTMLDivElement | null>(null)

    const hide = useCallback(() => {
        setHidden(true)
        window?.localStorage?.setItem(promptId, 'false')
        onPromptHide?.()
        debugPrompt('Prompt has become hidden.', debug)
    }, [promptId, onPromptHide, debug])
    const show = useCallback(() => {
        setHidden(false)
        window?.localStorage?.setItem(promptId, 'true')
        onPromptShow?.()
        debugPrompt('Prompt has become visible.', debug)
    }, [promptId, onPromptShow, debug])

    // Hide the prompt on keyboard escape press
    const hideOnEscape = useCallback(
        (e: KeyboardEvent) => {
            if (disableOutsideClick || e.key !== 'Escape') {
                return
            }

            debugPrompt('Escape key pressed, hiding prompt.', debug)
            hide()
        },
        [hide, disableOutsideClick, debug],
    )
    // Hide the prompt on outside click
    const hideOnOutsideClick = useCallback(
        (ev: MouseEvent) => {
            if (
                disableOutsideClick ||
                !backgroundComponentRef.current ||
                ev.target !== backgroundComponentRef.current
            ) {
                return
            }

            debugPrompt('Outside click detected, hiding prompt.', debug)
            hide()
            onOutsideClick?.()
        },
        [hide, disableOutsideClick, onOutsideClick, debug],
    )

    useEffect(() => {
        const cleanup = () => {
            document.body.removeEventListener('keyup', hideOnEscape)

            if (backgroundComponentRef.current) {
                backgroundComponentRef.current.removeEventListener(
                    'click',
                    hideOnOutsideClick,
                )
            }
        }

        // If it's being displayed, listen for escape key up
        if (!hidden) {
            document.body.addEventListener('keyup', hideOnEscape)

            if (backgroundComponentRef.current) {
                backgroundComponentRef.current.addEventListener(
                    'click',
                    hideOnOutsideClick,
                )
            }
        } else {
            cleanup()
        }

        return cleanup
    }, [hidden, hideOnEscape, hideOnOutsideClick, backgroundComponentRef])

    return {
        isHidden: hidden,
        hide,
        show,
        backgroundComponentRef,
    }
}

const debugPrompt = (
    message: string,
    debug?: debug.Debugger,
    ...args: any[]
) => {
    if (debug !== undefined) {
        debug(message, args)
    }
}
